import React, { Component } from 'react';
import { reduxForm, Field, FormSection } from 'redux-form';
import { get } from 'lodash';
import { FormattedMessage, injectIntl } from 'react-intl';

import ComitRightHandSide from '../../containers/commit-form/commit-right-hand-side.container';
import ModalSplitForm from '../../containers/modal-split-form.container';
import PayDepositOption from '../../containers/organizer-commit/pay-deposit-option.container';
import PaymentSourceSelector from '../../containers/commit-form/payment-source-selector.container';
import PBGUserAgreement from '../pbg-user-agreement';
import ShareInfo from '../../containers/commit-form/share-info.container';
import Signin from '../../containers/commit-form/signin.container';
import Signup from '../../containers/commit-form/signup.container';
import Title from '../title';
import TermsAndConditionsLink from '../../containers/terms-and-conditions-link';

import FormField from '../form-field';
import money, { Money, format } from '../../lib/money';
import validate from './validate';
import '../../css/commit-form.css';

class CommitForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      payDeposit: props.payDeposit,
      showGuestSignup: false,
    };

    this.resetForm = true;
    this.onEditMemberFields = this.onEditMemberFields.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onSkip = this.onSkip.bind(this);
    this.cardTokenized = this.props.cardTokenized.bind(this);
    this.onLogout = this.onLogout.bind(this);
    this.onHideSigninForm = this.onHideSigninForm.bind(this);
  }

  componentWillUnmount() {
    if (this.resetForm) {
      this.props.destroy();
    }
  }

  onHideSigninForm() {
    this.props.change('email', '');
    this.props.change('password', '');
  }

  onEditMemberFields() {
    this.resetForm = false;
    this.props.onEditMemberFields();
  }

  onSubmit(data) {
    const allowedAmounts = {
      allowedShareAmount: this.membershipMaxShareAmount,
      allowedFees: this.props.currentMembership.maxFees,
    };

    this.props.onSubmit({ ...data, ...allowedAmounts });
  }

  onLogout() {
    this.props.onLogout();
  }

  onSkip(data) {
    this.props.onSkip(data);
  }

  getMerchantName() {
    return get(this.props, 'purchase.merchant.displayName', '[Merchant Name]');
  }

  showingDeposit() { return this.state.payDeposit; }

  dontPayDeposit() {
    this.setState({ payDeposit: false });
  }

  payDeposit() { this.setState({ payDeposit: true }); }

  get membershipMaxAmount() {
    const { purchase, currentMembership, depositPayment } = this.props;
    const zeroAmount = money(0, purchase.currencyCode);
    const allowedAmount = money(get(currentMembership, 'maxAmount') || zeroAmount);
    const depositPaymentAmount = this.state.payDeposit ? money(get(depositPayment, 'amount')) : null;

    return Money.max([allowedAmount, (depositPaymentAmount || zeroAmount)]);
  }

  get membershipMaxShareAmount() {
    const { purchase, currentMembership, depositPayment } = this.props;
    const zeroAmount = money(0, purchase.currencyCode);
    const allowedShareAmount = money(get(currentMembership, 'maxShareAmount') || zeroAmount);
    const depositPaymentAmount = this.state.payDeposit ? money(get(depositPayment, 'amount')) : null;

    return Money.max([allowedShareAmount, (depositPaymentAmount || zeroAmount)]);
  }

  get footer() {
    return (
      <div className="row form-footer">
        <div className="col-12"><hr /></div>
        <div className="col-4">
          <p className="small-print">
            <FormattedMessage
              id="commitForm.userAgreemetAcceptance"
              defaultMessage={`By clicking on "I'm in" you confirm you accept the {ua}
              and {merchant} {tos}`}
              values={{
                ua: <PBGUserAgreement />,
                merchant: this.getMerchantName(),
                tos: <TermsAndConditionsLink />
              }}
            />
          </p>
        </div>
        <div className={this.props.allowSkip ? "col-4 flex-last" : "col-8"}>
          <button
            type="submit"
            disabled={this.props.submitDisabled}
            className="btn btn-primary pull-right"
            onClick={() => {
              this.props.change('skip', false);
              return true;
            }}
          >
            <FormattedMessage
              id="commitForm.submit"
              defaultMessage="I'm in for"
            /> {format(this.membershipMaxAmount)}
          </button>
        </div>
        {
          this.props.allowSkip && (
            <div className="col-4">
              {this.renderJoinLater()}
            </div>
          )
        }
      </div>
    );
  }

  renderOptInMarketing() {
    if (!this.props.showOptInMarketing) return null;
    if (this.props.membershipOptedIn) return null;

    return (
      <div className="row">
        <div className="col-12">
          <Field
            name="optInMarketing"
            type="checkbox"
            component={FormField}
            label={
              <FormattedMessage
                id="commitForm.optInMarketing.label"
                defaultMessage="Receive information and specials from {merchant}"
                values={{ merchant: this.getMerchantName() }}
              />
            }
          />
        </div>
      </div>
    );
  }

  renderForm() {
    const {
      handleSubmit, editing,
    } = this.props;

    return (
      <div className="commit-form">
        <form onSubmit={handleSubmit(this.onSubmit)}>
          <div className="form-group">
            <div className="d-inline-block">
              <ShareInfo
                editing={editing}
                claimedSlots={this.props.claimedSlots}
              />
            </div>

            {this.props.hasMemberFields &&
              <button
                type="button"
                className="btn btn-secondary greyish pull-right"
                onClick={this.onEditMemberFields}
                disabled={this.props.submitDisabled}
              >
                <FormattedMessage
                  id="commitForm.editMemberFieldsButton"
                  defaultMessage="Edit"
                />
              </button>
            }
          </div>

          <PayDepositOption
            onDeposit={() => this.payDeposit()}
            onNoDeposit={() => this.dontPayDeposit()}
            shouldRender={this.props.allowPayDeposit}
          />

          <Title
            text={
              <FormattedMessage
                id="commitForm.paymentMethod.title"
                defaultMessage="Payment Method"
              />
            }
            divider />
          {this.renderSignin()}

          <FormSection name="card">
            <PaymentSourceSelector
              cardTokenized={this.cardTokenized}
              cardError={this.props.cardError}
            />
          </FormSection>

          {this.renderSignup()}
          {this.renderOptInMarketing()}
          {this.footer}
        </form>
      </div>
    );
  }

  renderJoinLater() {
    const { purchase, allowSkip } = this.props;
    if (!allowSkip) return null;
    return (
      <button
        type="submit"
        className="btn btn-link text-muted px-0 join-later"
        disabled={this.props.submitDisabled}
        onClick={() => {
          this.props.change('skip', true);
          return true;
        }}
      >
        <FormattedMessage
          id="commitForm.skipForNow"
          defaultMessage="Skip for now"
        />
      </button>
    );
  }

  renderSignin() {
    if (!this.props.allowSignin) return null;

    return (
      <Signin
        onLogin={this.props.onLogin}
        onLogout={this.onLogout}
        onHideSigninForm={this.onHideSigninForm}
      />
    );
  }

  renderSignup() {
    return (
      <FormSection name="auth">
        <Signup
          onFacebookConnect={this.props.onLogin}
        />
        <Field component="input" type="hidden" name="needToLogin" />
      </FormSection>
    );
  }

  render() {
    return (
      <ModalSplitForm
        leftForm={this.renderForm()}
        rightForm={<ComitRightHandSide showingDeposit={this.showingDeposit()} />}
      />
    );
  }
}

CommitForm.defaultProps = {
  allowSignin: false,
  allowSkip: false,
  payDeposit: false,
  skip: false,
  showOptInMarketing: true,
  membershipOptedIn: false,
  depositPayment: null,
  onLogin: () => { },
  onLogout: () => { },
};

export const FORM_NAME = 'commitForm';

export default injectIntl(reduxForm({
  form: FORM_NAME,
  destroyOnUnmount: false,
  validate,
})(CommitForm));



// WEBPACK FOOTER //
// ./src/desktop/components/commit-form/index.js